import React, { useMemo } from "react";
import { CheckCircle2, Ban, XCircle, Clock } from "lucide-react";

/**
 * Status history — timeline + 12-month sparkline for a single facility.
 *
 * Two visualisations from the SAME audit-log data:
 *
 *   1. Timeline (list): every status change, newest first, with the actor,
 *      the reason, and an exact UTC timestamp.
 *
 *   2. Sparkline (row of bars): the last 12 months as vertical bars, each
 *      coloured by the STATUS the facility spent most of that month in
 *      (green = ACTIVE, amber = SUSPENDED, red = TERMINATED). Height
 *      encodes days spent in that dominant status, so a suspend halfway
 *      through the month reads as a half-height amber bar next to a
 *      full-height green bar for the previous month. Hover a bar to see
 *      the exact split.
 */
const TIER_META = {
  ACTIVE:     { icon: CheckCircle2, colour: "text-emerald-700", dot: "bg-emerald-500", bar: "bg-emerald-500", label: "Reactivated" },
  SUSPENDED:  { icon: Ban,          colour: "text-amber-700",   dot: "bg-amber-500",   bar: "bg-amber-500",   label: "Suspended" },
  TERMINATED: { icon: XCircle,      colour: "text-red-700",     dot: "bg-red-600",     bar: "bg-red-600",     label: "Terminated" },
};

const monthKey = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;

/** Given the ordered list of events + today, compute per-month days-in-each-status
 *  for the last 12 months. Returns an array of 12 items oldest→newest. */
function computeMonthlyBuckets(events) {
  const now = new Date();
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    months.push({
      key: monthKey(d),
      start: d,
      end: new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)),
      days: { ACTIVE: 0, SUSPENDED: 0, TERMINATED: 0 },
    });
  }

  // Build a chronological status series: [{ from: Date, status }]
  const series = [...(events || [])]
    .filter((e) => e.new_status)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    .map((e) => ({ from: new Date(e.timestamp), status: e.new_status }));

  // Assume ACTIVE prior to the first event
  const first = series.length ? series[0].from : new Date();
  const boundaries = [{ from: new Date(Math.min(first.getTime(), months[0].start.getTime())), status: "ACTIVE" }, ...series];

  months.forEach((m) => {
    for (let i = 0; i < boundaries.length; i++) {
      const b = boundaries[i];
      const next = boundaries[i + 1]?.from ?? new Date(Date.UTC(9999, 0, 1));
      const overlapStart = new Date(Math.max(b.from.getTime(), m.start.getTime()));
      const overlapEnd = new Date(Math.min(next.getTime(), m.end.getTime()));
      if (overlapEnd > overlapStart) {
        const days = (overlapEnd - overlapStart) / (1000 * 60 * 60 * 24);
        m.days[b.status] = (m.days[b.status] || 0) + days;
      }
    }
  });
  return months;
}

const shortMonth = (d) => d.toLocaleString("en", { month: "short" });

export const StatusHistory = ({ events, loading }) => {
  const months = useMemo(() => computeMonthlyBuckets(events || []), [events]);
  const maxDays = 31;

  if (loading) {
    return (
      <div className="text-xs italic text-slate-500 flex items-center gap-2">
        <Clock size={12} className="animate-spin" /> Loading status history…
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
        No status changes recorded for this facility.
      </div>
    );
  }

  const reversed = [...events].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div data-testid="status-history" className="space-y-4">
      {/* Sparkline — 12-month bars */}
      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
          Last 12 months
        </div>
        <div className="flex items-end gap-1 h-16">
          {months.map((m) => {
            const total = m.days.ACTIVE + m.days.SUSPENDED + m.days.TERMINATED;
            // Dominant status decides the bar colour
            const dominant = Object.entries(m.days).sort((a, b) => b[1] - a[1])[0]?.[0] || "ACTIVE";
            const meta = TIER_META[dominant];
            const height = Math.max(6, Math.round((total / maxDays) * 56));
            const title = `${m.key}  •  active ${Math.round(m.days.ACTIVE)}d, suspended ${Math.round(m.days.SUSPENDED)}d, terminated ${Math.round(m.days.TERMINATED)}d`;
            return (
              <div key={m.key} className="flex-1 flex flex-col items-center gap-1" title={title}>
                <div
                  className={`w-full rounded-t-sm ${meta.bar} transition-all`}
                  style={{ height: `${height}px` }}
                  data-testid={`spark-bar-${m.key}`}
                />
                <div className="text-[9px] font-mono text-slate-500 tracking-tight">
                  {shortMonth(m.start)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex gap-3 text-[10px] text-slate-600">
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" /> Active</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-amber-500" /> Suspended</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-red-600" /> Terminated</span>
        </div>
      </div>

      {/* Timeline — event list */}
      <div>
        <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
          Events ({events.length})
        </div>
        <ol className="relative border-l-2 border-slate-200 pl-4 space-y-4">
          {reversed.map((e, idx) => {
            const meta = TIER_META[e.new_status] || TIER_META.ACTIVE;
            const Icon = meta.icon;
            const ts = e.timestamp?.slice(0, 19).replace("T", " ") + " UTC";
            return (
              <li key={e.audit_id || idx} data-testid={`history-event-${idx}`} className="relative">
                <span className={`absolute -left-[22px] top-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full ${meta.dot} ring-2 ring-white`}>
                  <Icon size={10} className="text-white" strokeWidth={3} />
                </span>
                <div className={`text-[11px] font-bold uppercase tracking-wider ${meta.colour}`}>
                  {meta.label}
                </div>
                <div className="text-xs text-slate-800 mt-0.5">{e.reason || <span className="italic text-slate-400">no reason recorded</span>}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {e.actor_name || "—"} · <span className="font-mono">{ts}</span>{e.channel ? <> · <span className="uppercase">{e.channel}</span></> : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};
